import { createTRPCClient, httpBatchLink, TRPCClientError } from "@trpc/client";
import superjson from "superjson";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AppRouter } from "@notes/api/src/routers.js";

/**
 * Cliente tRPC tipado a partir do router do servidor: mudar a API quebra o
 * typecheck da web, e não a tela do usuário em produção.
 */
export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: "/trpc",
      transformer: superjson,
      headers() {
        const agencia = localStorage.getItem("enotes:agencia");
        // O cabeçalho ESCOLHE entre as agências permitidas; o servidor confere.
        return agencia ? { "x-tenant-id": agencia } : {};
      },
    }),
  ],
});

/** Mensagem que o usuário lê. O detalhe técnico fica no console, não na tela. */
export function mensagemDeErro(e: unknown): string {
  if (e instanceof TRPCClientError) {
    const codigo = (e.data as { code?: string } | undefined)?.code;
    if (codigo === "UNAUTHORIZED") return "Sua sessão expirou. Entre de novo.";
    if (codigo === "FORBIDDEN") return e.message;
    if (codigo === "BAD_REQUEST") return e.message;
    return "Não consegui carregar. Tente de novo em instantes.";
  }
  return "Não consegui falar com o servidor.";
}

/** Atalhos de consulta, para a tela não repetir chave de cache. */
export const usarReunioes = () =>
  useQuery({ queryKey: ["reunioes"], queryFn: () => trpc.reunioes.listar.query() });

/**
 * Tentar de novo uma reunião que travou. Reabre só o que falhou; a varredura do
 * operário retoma daí. Invalida a reunião para a tela mostrar o estado novo em
 * vez do otimismo local.
 */
export const usarReprocessar = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string }) => trpc.reunioes.reprocessar.mutate(v),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ["reuniao", v.id] });
      void qc.invalidateQueries({ queryKey: ["reunioes"] });
    },
  });
};

export const usarReuniao = (id: string | null) =>
  useQuery({
    queryKey: ["reuniao", id],
    queryFn: () => trpc.reunioes.detalhe.query({ id: id! }),
    enabled: !!id,
  });

export const usarResumoDoDia = () =>
  useQuery({ queryKey: ["resumo-do-dia"], queryFn: () => trpc.reunioes.resumoDoDia.query({ dias: 7 }) });

export const usarPendencias = () =>
  useQuery({ queryKey: ["pendencias"], queryFn: () => trpc.reunioes.pendencias.query() });

export const usarAprovacoes = () =>
  useQuery({ queryKey: ["aprovacoes"], queryFn: () => trpc.aprovacoes.pendentes.query() });

/**
 * Telefonia. `estado` é consultado sempre que a tela abre porque ele decide se
 * o botão de ligar aparece; sem o módulo, a resposta é um erro de permissão e
 * a tela simplesmente não mostra nada de voz.
 */
export const usarVoz = () =>
  useQuery({
    queryKey: ["voz", "estado"],
    queryFn: () => trpc.voz.estado.query(),
    // Agência sem telefonia responde 403 a cada montagem de tela. Uma
    // tentativa basta: repetir enche o log do servidor com o previsível.
    retry: false,
  });

export const usarLigacoes = (somenteMinhas = true) =>
  useQuery({
    queryKey: ["voz", "ligacoes", somenteMinhas],
    queryFn: () => trpc.voz.ligacoes.query({ somenteMinhas, limite: 100 }),
    retry: false,
  });

export const usarPonte = () =>
  useQuery({ queryKey: ["ponte"], queryFn: () => trpc.agencia.statusDaPonte.query() });

/**
 * O relatório da ponte. Atualiza sozinho a cada meio minuto porque a tela é
 * onde a pessoa olha quando desconfia que algo parou: número velho ali é pior
 * que número nenhum.
 */
export const usarRelatorioDaPonte = (horas = 24) =>
  useQuery({
    queryKey: ["ponte", "relatorio", horas],
    queryFn: () => trpc.agencia.relatorioDaPonte.query({ horas }),
    refetchInterval: 30_000,
  });

/** "A ponte está ligada?" para qualquer membro: decide se o botão CRM aparece. */
export const usarPontePronta = () =>
  useQuery({ queryKey: ["ponte", "pronta"], queryFn: () => trpc.agencia.pontePronta.query(), staleTime: 5 * 60_000 });

/** Abre o CRM já logado, em nova aba, pelo SSO da ponte. */
export async function abrirNoCrm(alvo: { tipo: "home" | "reuniao" | "contato" | "negociacao"; id?: string | number } = { tipo: "home" }): Promise<void> {
  // A aba abre ANTES da chamada: navegador bloqueia window.open fora do clique.
  const aba = window.open("", "_blank", "noopener");
  try {
    const r = await trpc.agencia.abrirNoCrm.mutate({ alvo });
    if (aba) aba.location.href = r.url; else window.open(r.url, "_blank", "noopener");
  } catch (e) {
    aba?.close();
    throw e;
  }
}

export function usarDecidirAcao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { acaoId: string; decisao: "aprovada" | "rejeitada"; motivo?: string }) =>
      trpc.aprovacoes.decidir.mutate(v),
    // Decidir muda a lista de aprovações E a de pendências: invalidar as duas
    // evita a tela mostrar um item que já não existe.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["aprovacoes"] });
      void qc.invalidateQueries({ queryKey: ["pendencias"] });
      void qc.invalidateQueries({ queryKey: ["reuniao"] });
    },
  });
}
